import React from 'react';

/**
 * A placeholder whose value differs per person (owner, 2026-10-21: the
 * per-industry new tech offer count read as "wrong" next to platform-wide
 * totals). The server's memac.offer_count_buckets says who would see which
 * number; this shows it before anything is sent. Optional everywhere: no
 * data, nothing shown.
 */
export type OfferBucket = { value: string; label: string; people: number };
export type OfferBuckets = { total: string; varies: boolean; buckets: OfferBucket[]; people: number };

const fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString('en-IN') : String(n));

export const VaryingNotice: React.FC<{ name: string; data?: OfferBuckets | null; className?: string }> = ({ name, data, className }) => {
  if (!data) return null;
  const buckets = Array.isArray(data.buckets) ? data.buckets : [];
  return (
    <div className={`text-xs rounded-lg px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800 ${className || ''}`}>
      <div>
        <span className="font-mono">{`{${name}}`}</span> is different for each person — this is what they will see
        {data.total ? <> (the platform total is {data.total})</> : null}:
      </div>
      {buckets.length === 0 ? (
        <div className="mt-1 text-amber-700">No recipients yet: pick the audience to see the breakdown.</div>
      ) : (
        <table className="mt-1 w-full text-left">
          <thead>
            <tr className="text-[11px] text-amber-700">
              <th className="pr-3 font-semibold">Value</th>
              <th className="pr-3 font-semibold">Who</th>
              <th className="text-right font-semibold">People</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((b, i) => (
              <tr key={`${b.value}-${i}`} className="align-top">
                <td className="pr-3 font-mono whitespace-nowrap">{b.value}</td>
                <td className="pr-3">{b.label}</td>
                <td className="text-right whitespace-nowrap">{fmt(b.people)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** Every varying placeholder of a preview response ({name: buckets}). */
export const VaryingNotices: React.FC<{ varying?: Record<string, OfferBuckets> | null; className?: string }> = ({ varying, className }) => {
  if (!varying || typeof varying !== 'object') return null;
  const names = Object.keys(varying);
  if (!names.length) return null;
  return (
    <div className={`flex flex-col gap-2 ${className || ''}`}>
      {names.map(n => <VaryingNotice key={n} name={n} data={varying[n]} />)}
    </div>
  );
};
